import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Link, Route, Routes, useLocation } from "react-router-dom";
import AdminPage from "@/AdminPage";
import Nav from "@/Nav";
import QuickActions from "@/QuickActions";
import { BlogList, BlogPost } from "@/Blog";
import LegalPage from "@/LegalPages";
import CookieConsent from "@/CookieConsent";
import { PHONE, WHATSAPP, EMAIL, WHATSAPP_LINK } from "@/shared";
import "@/App.css";
import axios from "axios";
import Lenis from "lenis";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Clock3, Download, Droplets, Facebook, HeartHandshake, Instagram, Mail, MapPin, MessageCircle, Phone, ShieldCheck, Star, Wrench, X, Zap } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EASE = [0.16, 1, 0.3, 1];

const chapters = [
  { num: "01", icon: Zap, title: "Emergency service", text: "Your purifier stopped? We move first. Priority callouts across Raipur, same day wherever possible.", tag: "Priority response" },
  { num: "02", icon: ShieldCheck, title: "AMC plans", text: "One plan, zero worry. Scheduled filter changes, sanitisation and checkups — we remember so you don't have to.", tag: "Yearly care" },
  { num: "03", icon: Wrench, title: "Installation", text: "Measured, mounted, tested. Professional setup with TDS checks and clear guidance before we leave.", tag: "Done right" },
  { num: "04", icon: Droplets, title: "New RO purchase", text: "Honest advice on the right purifier for your water, your family and your budget — no pushy upsells.", tag: "Sales & guidance" },
];

function Mask({ children, delay = 0 }) {
  return (
    <span className="mask">
      <motion.span className="mask-inner" initial={{ y: "112%" }} animate={{ y: "0%" }} transition={{ duration: 1.05, delay, ease: EASE }}>
        {children}
      </motion.span>
    </span>
  );
}

function FadeUp({ children, delay = 0, className }) {
  return (
    <motion.div className={className} initial={{ opacity: 0, y: 42 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.9, delay, ease: EASE }}>
      {children}
    </motion.div>
  );
}

function RainDrops() {
  const drops = Array.from({ length: 14 }, (_, i) => ({
    left: `${(i * 73 + 11) % 100}%`,
    duration: `${4 + (i % 5) * 1.3}s`,
    delay: `${((i * 0.9) % 7).toFixed(1)}s`,
    scale: 0.6 + (i % 4) * 0.2,
  }));
  return (
    <div className="droplets" aria-hidden="true">
      {drops.map((d, i) => <span key={i} style={{ left: d.left, animationDuration: d.duration, animationDelay: d.delay, "--ds": d.scale }}/>)}
    </div>
  );
}

function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [6, -6]), { stiffness: 130, damping: 17 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-8, 8]), { stiffness: 130, damping: 17 });
  const move = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  return (
    <section id="home" className="hero" ref={ref} onMouseMove={move}>
      <div className="hero-glow"/>
      <RainDrops/>
      <div className="container hero-grid">
        <div className="hero-copy">
          <motion.p className="kicker" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15, ease: EASE }}><span/> Raipur's trusted RO specialists</motion.p>
          <h1 className="hero-title">
            <Mask delay={0.25}>Pure water.</Mask>
            <Mask delay={0.42}><em>peace of mind.</em></Mask>
          </h1>
          <motion.p className="hero-sub" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.72, ease: EASE }}>From an emergency repair to a brand-new installation — Parth Enterprises keeps your water flowing clean, clear and safe.</motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.88, ease: EASE }}>
            <a data-testid="hero-call-button" className="btn btn-primary" href={`tel:${PHONE}`}><Phone size={16}/> Call for service</a>
            <a data-testid="hero-enquiry-link" className="btn btn-ghost" href="#contact">Book an enquiry <ArrowRight size={15}/></a>
          </motion.div>
          <motion.div className="hero-trust" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.1 }}>
            <div className="avatar-stack"><span>R</span><span>A</span><span>S</span><span>+</span></div>
            <span><strong>4.9/5</strong> loved by Raipur homes</span>
          </motion.div>
        </div>
        <motion.div className="hero-visual" style={{ rotateX, rotateY, transformPerspective: 1000 }} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, delay: 0.55, ease: EASE }}>
          <div className="hero-spot"/>
          <div className="hero-clip"><img data-testid="hero-service-image" className="hero-img" src="https://images.unsplash.com/photo-1659346435902-9bd10146b5d9?auto=format&fit=crop&w=1000&q=85" alt="RO water purifier installed on a kitchen counter"/><span className="img-mark"><Droplets size={12}/> Parth Enterprises</span></div>
          <motion.div className="hero-chip" animate={{ y: [0, -9, 0] }} transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut" }}>
            <span className="chip-dot"><Check size={14}/></span>
            <div><strong>Service you can trust</strong><small>Skilled technicians · Honest advice</small></div>
          </motion.div>
        </motion.div>
      </div>
      <motion.div className="scroll-cue" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }}><span/>Scroll</motion.div>
    </section>
  );
}

function Marquee() {
  const items = ["Emergency service", "AMC plans", "Installation", "New RO purchase", "Filter replacement", "Same-day support"];
  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee-track">{[...items, ...items].map((t, i) => <span key={i}>{t} <Droplets size={15}/></span>)}</div>
    </div>
  );
}

function Chapters() {
  return (
    <section id="services" className="chapters">
      <span className="bg-watermark" aria-hidden="true">Parth Enterprises</span>
      <div className="container">
        <FadeUp><p className="kicker"><span/> What we do</p>
        <h2 className="section-title">Four ways we<br/><em>care for your water.</em></h2></FadeUp>
        <div className="chapter-list">
          {chapters.map((c, i) => (
            <FadeUp key={c.num} delay={i * 0.07}>
              <a data-testid={`service-card-${i + 1}`} href="#contact" className="chapter">
                <span className="chapter-num">{c.num}</span>
                <span className="chapter-body"><h3>{c.title}</h3><p>{c.text}</p></span>
                <span className="chapter-tag">{c.tag}</span>
                <span className="chapter-arrow"><ArrowUpRight size={19}/></span>
              </a>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

const BRANDS = [
  "Aquaguard RO", "Kent RO", "Eurotouch RO", "Eurotech RO", "Oliver International RO",
  "Aquasafe RO", "Aquagrand RO", "Livpure RO", "Havells RO", "LG RO", "Nasaka RO",
];

function BrandsShop() {
  return (
    <section id="brands" className="brands">
      <div className="container brands-grid">
        <div>
          <FadeUp>
            <p className="kicker"><span/> Top water purifier shop near me</p>
            <h2 className="section-title">Expert RO service &<br/><em>repair in Raipur.</em></h2>
            <p className="body-copy">Looking for an RO shop near me? We are Raipur's premier water purifier shop and water cooler shop — domestic repair, RO plant service (100–500 LPH), and maintenance for brands like Aquaguard, Havells, Livpure, Eurotech and Oliver International RO.</p>
            <p className="body-copy">Searching for a reliable water purifier shop near me? Our certified technicians handle everything from domestic RO maintenance to industrial RO plant service. Ensure your family's health with our affordable RO maintenance plans.</p>
          </FadeUp>
          <FadeUp delay={0.1} className="badge-row">
            <span className="badge-chip" data-testid="badge-genuine-spares"><ShieldCheck size={15}/> 100% Genuine Spares</span>
            <span className="badge-chip" data-testid="badge-fast-service"><Zap size={15}/> Fast Service Near Me</span>
          </FadeUp>
          <FadeUp delay={0.16} className="hero-actions">
            <a data-testid="brands-call-button" className="btn btn-primary" href={`tel:${PHONE}`}><Phone size={16}/> Call RO Shop Near Me</a>
            <a data-testid="brands-services-link" className="btn btn-ghost" href="/#services">View RO Plant Services <ArrowRight size={15}/></a>
          </FadeUp>
        </div>
        <FadeUp delay={0.12} className="brand-panel">
          <h3>Brands repaired at our RO shop near me</h3>
          <div className="brand-grid">
            {BRANDS.map((b) => <span key={b} className="brand-chip" data-testid={`brand-${b.toLowerCase().replace(/\s+/g, "-")}`}><Check size={13}/> {b}</span>)}
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function Emergency() {
  return (
    <section className="emergency">
      <div className="container emergency-inner">
        <FadeUp className="emergency-copy">
          <h2 className="emergency-title">Purifier stopped working?</h2>
          <p>Priority emergency support across Raipur — call or message, we move fast.</p>
        </FadeUp>
        <FadeUp delay={0.12} className="emergency-cta">
          <a data-testid="emergency-whatsapp-button" href={WHATSAPP_LINK} target="_blank" rel="noreferrer"><Zap size={16}/> Message on WhatsApp</a>
          <a className="emergency-call" href={`tel:${PHONE}`}><Phone size={16}/> {PHONE}</a>
        </FadeUp>
      </div>
    </section>
  );
}

function Spotlight() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-38, 38]);
  return (
    <section id="why-us" className="spotlight" ref={ref}>
      <div className="container spotlight-grid">
        <FadeUp className="spotlight-frame">
          <div className="spotlight-clip">
            <motion.img style={{ y }} data-testid="technician-image" src="https://images.unsplash.com/photo-1655808166424-9b543b6982fa?auto=format&fit=crop&w=900&q=85" alt="Under-sink RO water purifier installed in a kitchen"/>
            <span className="img-mark"><Droplets size={12}/> Parth Enterprises</span>
          </div>
          <div className="experience-badge"><strong>10+</strong><span>years of<br/>experience</span></div>
        </FadeUp>
        <div className="spotlight-copy">
          <FadeUp><p className="kicker"><span/> The Parth promise</p>
          <h2 className="section-title">Good service is<br/><em>clear service.</em></h2>
          <p className="body-copy">We believe you deserve straight answers, skilled hands and water you can feel good about. No confusing jargon. No unnecessary parts. Just honest work, done carefully.</p></FadeUp>
          <FadeUp delay={0.1} className="promise-list">
            <div><HeartHandshake size={20}/><span><strong>Honest recommendations</strong><small>We fix what's needed, not what isn't.</small></span></div>
            <div><ShieldCheck size={20}/><span><strong>Qualified technicians</strong><small>Careful work, tested before we leave.</small></span></div>
          </FadeUp>
          <FadeUp delay={0.18}><a data-testid="why-us-contact-link" className="text-link" href="#contact">Talk to our team <ArrowRight size={15}/></a></FadeUp>
        </div>
      </div>
    </section>
  );
}

const AREAS = [
  "Shankar Nagar", "Civil Lines", "Devendra Nagar", "Samta Colony", "Pandri", "Telibandh", "Tatibandh",
  "Rajendra Nagar", "Gudhiyari", "Bhatagaon", "Mowa", "Saddu", "Kabir Nagar", "Kota", "Tikrapara",
  "Pachpedi Naka", "Fafadih", "Byron Bazar", "Vidhan Sabha Road", "Avanti Vihar", "Khamardih",
  "Bhanpuri", "Birgaon", "Naya Raipur", "Raipura", "Changurabhata", "Santoshi Nagar", "Mathpuraina",
];

function ServiceAreas() {
  return (
    <section id="areas" className="areas">
      <div className="container">
        <FadeUp>
          <p className="kicker"><span/> Local RO service near me</p>
          <h2 className="section-title">Find the best water purifier<br/><em>shop in Raipur.</em></h2>
          <p className="body-copy">From RO plant service to domestic RO maintenance, our experts reach every neighborhood across Raipur quickly and efficiently.</p>
        </FadeUp>
        <FadeUp delay={0.1} className="area-grid">
          {AREAS.map((a) => <span key={a} className="area-chip" data-testid={`area-${a.toLowerCase().replace(/\s+/g, "-")}`}><MapPin size={13}/> {a}</span>)}
        </FadeUp>
        <FadeUp delay={0.16}>
          <p className="areas-note">Looking for a water cooler shop or RO shop near me? <b>We've got you covered!</b> <a data-testid="areas-call-link" className="text-link" href={`tel:${PHONE}`}>Call {PHONE} <ArrowRight size={15}/></a></p>
        </FadeUp>
      </div>
    </section>
  );
}

function Testimonials() {
  const quotes = [
    { text: "They arrived the same day, explained the issue simply and had our RO running before dinner.", author: "Ritu Sharma", place: "Purani Basti" },
    { text: "Very professional installation. The team was punctual, neat and genuinely helpful.", author: "Amit Jain", place: "Raipur" },
  ];
  return (
    <section id="testimonials" className="testimonials">
      <span className="bg-watermark" aria-hidden="true">Pure Water</span>
      <div className="container">
        <FadeUp><p className="kicker"><span/> Kind words</p>
        <h2 className="section-title">Trusted by<br/><em>Raipur homes.</em></h2></FadeUp>
        <div className="testimonial-grid">
          {quotes.map((q, i) => (
            <FadeUp key={q.author} delay={i * 0.1}>
              <article data-testid={`testimonial-card-${i + 1}`} className="t-card">
                <div className="stars"><Star size={14}/><Star size={14}/><Star size={14}/><Star size={14}/><Star size={14}/></div>
                <blockquote>“{q.text}”</blockquote>
                <strong>— {q.author} <span>· {q.place}</span></strong>
              </article>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function EnquiryForm({ prefix = "enquiry" }) {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", service: "Emergency service", message: "" });
  useEffect(() => {
    setForm((f) => {
      if (f.name && f.phone) return f;
      let saved = {};
      try { saved = JSON.parse(localStorage.getItem("parth_contact") || "{}"); } catch {}
      return { ...f, name: f.name || saved.name || "", phone: f.phone || saved.phone || "" };
    });
  }, []);
  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setSent(false); setError(false);
    const waText = `New enquiry — Parth Enterprises\nName: ${form.name}\nPhone: ${form.phone}\nService: ${form.service}\nMessage: ${form.message || "—"}`;
    window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(waText)}`, "_blank", "noopener");
    try {
      await axios.post(`${API}/enquiries`, form, { withCredentials: true });
      setSent(true);
      try { localStorage.setItem("parth_contact", JSON.stringify({ name: form.name, phone: form.phone })); } catch {}
      setForm({ name: "", phone: "", service: "Emergency service", message: "" });
    }
    catch { setError(true); }
    finally { setLoading(false); }
  };
  return (
    <form data-testid={`${prefix}-form`} className="enquiry-form" onSubmit={submit} autoComplete="on">
      <label>Your name<input data-testid={`${prefix}-name-input`} required autoComplete="name" value={form.name} onChange={update("name")} placeholder="e.g. Priya Sharma"/></label>
      <label>Phone number<input data-testid={`${prefix}-phone-input`} required type="tel" autoComplete="tel" value={form.phone} onChange={update("phone")} placeholder="Your 10-digit number"/></label>
      <label>What can we help with?<select data-testid={`${prefix}-service-select`} value={form.service} onChange={update("service")}><option>Emergency service</option><option>AMC plan</option><option>Installation</option><option>New RO purchase</option><option>Filter replacement</option></select></label>
      <label>Anything else? <span>(optional)</span><textarea data-testid={`${prefix}-message-input`} value={form.message} onChange={update("message")} placeholder="Tell us a little about your purifier..."/></label>
      <button data-testid={`${prefix}-submit-button`} className="btn btn-primary full" type="submit" disabled={loading}>{loading ? "Sending..." : "Send enquiry"} <ArrowRight size={16}/></button>
      {sent && <div data-testid={`${prefix}-success-message`} className="form-note success"><Check size={15}/> Enquiry sent! WhatsApp also opened with your details — just press send there.</div>}
      {error && <div data-testid={`${prefix}-error-message`} className="form-note error"><X size={15}/> We couldn't send that just now. Please try again or WhatsApp us.</div>}
    </form>
  );
}

function EnquiryPopup() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      let consent = null;
      try { consent = localStorage.getItem("parth_cookie_consent"); } catch {}
      if (consent) { setOpen(true); return; }
      const onConsent = () => setOpen(true);
      window.addEventListener("parth-consent", onConsent, { once: true });
    }, 5000);
    return () => clearTimeout(t);
  }, []);
  if (!open) return null;
  return (
    <div className="popup-overlay" data-testid="enquiry-popup" onClick={() => setOpen(false)}>
      <motion.div className="popup-card" onClick={(e) => e.stopPropagation()} initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.5, ease: EASE }}>
        <button data-testid="popup-close-button" className="popup-close" onClick={() => setOpen(false)} aria-label="Close popup"><X size={18}/></button>
        <p className="kicker"><span/> Book a service</p>
        <h3 className="popup-title">Need RO help? <em>Tell us.</em></h3>
        <EnquiryForm prefix="popup"/>
      </motion.div>
    </div>
  );
}

const FAQS = [
  ["What is the RO water purifier service cost in Raipur?", "Service charges depend on the issue — a basic service visit is very affordable, while filter, candle or membrane replacement is priced by part. We always share an honest, upfront quote before any work begins. Call 08602227528 for today's price."],
  ["Do you provide RO water purifier service at home?", "Yes — we offer doorstep RO water purifier service at home across Raipur. Our local technicians usually reach you within 60 minutes of booking, so you never carry your purifier anywhere."],
  ["Which brands do you service and repair?", "We service Kent, Aquaguard (Eureka Forbes), LG, Livpure, Havells, Blue Star, AO Smith, Pureit, Nasaka, Eurotouch, Eurotech, Oliver International and all other domestic RO, UV and UF water purifiers — with 100% genuine spare parts."],
  ["What is the RO annual maintenance (AMC) cost?", "Our AMC plans are budget-friendly and cover scheduled servicing, filter checks and priority breakdown support for the full year. The exact cost depends on your purifier model — message us on WhatsApp for a quick quote."],
  ["Are you open now? How do I contact you?", "We work Monday to Sunday, 8:00 AM to 9:00 PM — including weekends and most holidays. Call or WhatsApp 08602227528 anytime during working hours for same-day service."],
  ["Do you repair RO purifiers near me in Raipur?", "Almost certainly yes! We cover Shankar Nagar, Civil Lines, Telibandh, Pandri, Devendra Nagar, Samta Colony, Naya Raipur, Purani Basti and every other major locality in Raipur. See our service areas section or call to confirm."],
];

function Listing() {
  const tags = ["RO Service", "Water Purifier Repair", "AMC Plans", "Installation", "New RO Sales", "Filter Replacement"];
  return (
    <section id="listing" className="listing">
      <div className="container">
        <FadeUp>
          <div className="jd-card" data-testid="business-listing">
            <div className="jd-head">
              <span className="brand-mark jd-logo"><Droplets size={22}/></span>
              <div className="jd-title">
                <h3>Parth Enterprises <span className="jd-verified" data-testid="listing-verified"><Check size={12}/> Verified</span></h3>
                <p>Water Purifier Sales & Service · RO Shop</p>
              </div>
              <div className="jd-rating" data-testid="listing-rating">
                <strong>4.9</strong>
                <div className="jd-stars"><Star size={14}/><Star size={14}/><Star size={14}/><Star size={14}/><Star size={14}/></div>
                <span>27 Ratings</span>
              </div>
            </div>
            <div className="jd-meta">
              <span className="jd-open" data-testid="listing-hours"><Clock3 size={14}/> Open Now · 8:00 AM – 9:00 PM</span>
              <span><MapPin size={14}/> Bandhwapara, Purani Basti, Raipur</span>
              <span><ShieldCheck size={14}/> 10+ Years in Service</span>
            </div>
            <div className="jd-tags">{tags.map((t) => <span key={t}>{t}</span>)}</div>
            <div className="jd-actions">
              <a data-testid="listing-call-button" className="jd-call" href={`tel:${PHONE}`}><Phone size={16}/> Call Now</a>
              <a data-testid="listing-whatsapp-button" className="jd-wa" href={WHATSAPP_LINK} target="_blank" rel="noreferrer"><MessageCircle size={16}/> WhatsApp</a>
              <a data-testid="listing-enquiry-link" className="jd-enquiry" href="/#contact">Send Enquiry</a>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section id="faq" className="faq">
      <div className="container">
        <FadeUp><p className="kicker"><span/> Good to know</p>
        <h2 className="section-title">Frequently asked<br/><em>questions.</em></h2></FadeUp>
        <FadeUp delay={0.1} className="blog-faq faq-list">
          {FAQS.map(([q, a]) => <details key={q} data-testid={`faq-${q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}><summary>{q}</summary><p>{a}</p></details>)}
        </FadeUp>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" className="contact">
      <div className="container contact-grid">
        <div className="contact-copy">
          <FadeUp><p className="kicker"><span/> Let's get started</p>
          <h2 className="section-title">Tell us what<br/><em>you need.</em></h2>
          <p className="body-copy">Share a few details and our team will get back to you shortly.</p></FadeUp>
          <FadeUp delay={0.1} className="contact-details">
            <a data-testid="contact-phone-link" href={`tel:${PHONE}`}><Phone size={18}/><span>Call us<strong>{PHONE}</strong></span></a>
            <a data-testid="contact-whatsapp-link" href={WHATSAPP_LINK} target="_blank" rel="noreferrer"><MessageCircle size={18}/><span>WhatsApp<strong>Chat with our team</strong></span></a>
            <a data-testid="contact-email-link" href={`mailto:${EMAIL}`}><Mail size={18}/><span>Email us<strong>{EMAIL}</strong></span></a>
            <div data-testid="contact-address"><MapPin size={18}/><span>Visit us<strong>Bandhwapara, Purani Basti, Raipur</strong></span></div>
          </FadeUp>
        </div>
        <FadeUp delay={0.12}>
          <EnquiryForm/>
        </FadeUp>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer>
      <div className="container">
        <div className="footer-giant" aria-hidden="true">Parth Enterprises</div>
        <div className="footer-inner">
          <a data-testid="footer-brand-link" className="brand" href="#home"><span className="brand-mark"><Droplets size={19}/></span><span className="brand-word">Parth <b>Enterprises</b><small>Pure water. Prompt care.</small></span></a>
          <span className="footer-copy">Sales & Service · Bandhwapara, Purani Basti, Raipur, Chhattisgarh</span>
          <div className="socials"><a data-testid="footer-instagram-link" href="#home" aria-label="Instagram"><Instagram size={17}/></a><a data-testid="footer-facebook-link" href="#home" aria-label="Facebook"><Facebook size={17}/></a></div>
          <div className="legal-links"><Link data-testid="footer-privacy-link" to="/privacy-policy">Privacy Policy</Link><Link data-testid="footer-terms-link" to="/terms-conditions">Terms & Conditions</Link><Link data-testid="footer-disclaimer-link" to="/disclaimer">Disclaimer</Link></div>
        </div>
      </div>
    </footer>
  );
}

function Home({ onInstall }) {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"], a[href^="/#"]');
      if (!a) return;
      const hash = a.getAttribute("href").replace(/^\/#/, "#");
      const el = document.querySelector(hash);
      if (el) { e.preventDefault(); lenis.scrollTo(el, { offset: -90 }); }
    };
    document.addEventListener("click", onClick);
    if (window.location.hash) {
      const target = document.querySelector(window.location.hash);
      if (target) setTimeout(() => lenis.scrollTo(target, { offset: -90 }), 500);
    }
    return () => { cancelAnimationFrame(raf); lenis.destroy(); document.removeEventListener("click", onClick); };
  }, []);
  return (
    <main>
      <Nav/>
      <Hero/>
      <Marquee/>
      <Chapters/>
      <BrandsShop/>
      <Emergency/>
      <Spotlight/>
      <ServiceAreas/>
      <Testimonials/>
      <Listing/>
      <Faq/>
      <Contact/>
      <Footer/>
      <EnquiryPopup/>
      <QuickActions/>
      {onInstall && (
        <motion.button data-testid="install-app-button" className="install-fab" onClick={onInstall} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
          <Download size={15}/> Install app
        </motion.button>
      )}
    </main>
  );
}

function AppRouter() {
  const location = useLocation();
  const [installPrompt, setInstallPrompt] = useState(null);
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);
  const install = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    axios.post(`${API}/track`, { path: location.pathname }).catch(() => {});
  }, [location.pathname]);
  return (
    <>
      <Routes>
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/privacy-policy" element={<LegalPage page="privacy" />} />
        <Route path="/terms-conditions" element={<LegalPage page="terms" />} />
        <Route path="/disclaimer" element={<LegalPage page="disclaimer" />} />
        <Route path="*" element={<Home onInstall={installPrompt ? install : null} />} />
      </Routes>
      <CookieConsent/>
    </>
  );
}

function App() {
  return <BrowserRouter><AppRouter /></BrowserRouter>;
}

export default App;
